import { useReducer, useEffect, useCallback } from 'react';
import { usePrevious } from 'site-modules/shared/hooks/use-previous';

const SCREEN_STATES = {
  ZIP_FORM: 'zipForm',
  INVALID_RADIUS: 'invalidRadius',
  LEAD_FORM: 'leadForm',
  HIDDEN: 'hidden',
};

const ACTIONS = {
  START_LOADING: 'START_LOADING',
  RESOLVE_LEADS: 'RESOLVE_LEADS',
  MARK_ZIP_SUBMITTED: 'MARK_ZIP_SUBMITTED',
};

const screenConfigs = {
  [SCREEN_STATES.ZIP_FORM]: { noLeadTargets: true, showZipForm: true },
  [SCREEN_STATES.INVALID_RADIUS]: { noLeadTargets: true, showZipForm: false },
  [SCREEN_STATES.LEAD_FORM]: { noLeadTargets: false, showZipForm: false },
};

function getInitialScreen(leadTargets, isZipLongDistance) {
  if (leadTargets === undefined) {
    return SCREEN_STATES.HIDDEN;
  }

  if (isZipLongDistance) {
    return SCREEN_STATES.ZIP_FORM;
  }
  if (!leadTargets.length) {
    return SCREEN_STATES.HIDDEN;
  }

  return SCREEN_STATES.LEAD_FORM;
}

function initializeState({ leadTargets, isZipLongDistance }) {
  return {
    isLoading: leadTargets === undefined,
    currentScreen: getInitialScreen(leadTargets, isZipLongDistance),
    cachedLeadTargets: isZipLongDistance && leadTargets !== undefined ? [] : leadTargets || [],
    hasUserSubmittedZip: false,
  };
}

function determineNextScreen(leadTargets, isZipLongDistance, hasUserSubmittedZip) {
  // Case 1: User is in long-distance location and hasn't submitted zip yet
  // Show zip form to allow user to enter a closer location
  if (isZipLongDistance && !hasUserSubmittedZip) {
    return SCREEN_STATES.ZIP_FORM;
  }

  // Case 2: User submitted zip but no dealers found in the new radius
  // Show "invalid radius" message
  if (hasUserSubmittedZip && !leadTargets.length) {
    return SCREEN_STATES.INVALID_RADIUS;
  }

  // Case 3: Dealers are available
  // Show lead form to capture user information
  if (leadTargets.length) {
    return SCREEN_STATES.LEAD_FORM;
  }

  // Case 4: Fallback for all other scenarios
  // Hide the form (e.g., no dealers and user hasn't interacted)
  return SCREEN_STATES.HIDDEN;
}

function reducer(state, action) {
  switch (action.type) {
    case ACTIONS.START_LOADING:
      return { ...state, isLoading: true };

    case ACTIONS.RESOLVE_LEADS: {
      const { leadTargets, isZipLongDistance } = action.payload;
      const nextScreen = determineNextScreen(leadTargets, isZipLongDistance, state.hasUserSubmittedZip);

      return {
        ...state,
        isLoading: false,
        cachedLeadTargets: leadTargets,
        currentScreen: nextScreen,
        hasUserSubmittedZip: nextScreen === SCREEN_STATES.LEAD_FORM ? false : state.hasUserSubmittedZip,
      };
    }

    case ACTIONS.MARK_ZIP_SUBMITTED:
      return { ...state, hasUserSubmittedZip: true };

    default:
      return state;
  }
}

export const useLeadFormScreenState = (leadTargets, isZipLongDistance) => {
  const [state, dispatch] = useReducer(reducer, { leadTargets, isZipLongDistance }, initializeState);

  const previousLeadTargets = usePrevious(leadTargets);

  useEffect(() => {
    if (leadTargets === previousLeadTargets) return;
    if (leadTargets === undefined) {
      dispatch({ type: ACTIONS.START_LOADING });
      return;
    }

    dispatch({
      type: ACTIONS.RESOLVE_LEADS,
      payload: { leadTargets, isZipLongDistance },
    });
    // eslint-disable-next-line
  }, [leadTargets]);

  const handleZipSubmit = useCallback(() => {
    dispatch({ type: ACTIONS.MARK_ZIP_SUBMITTED });
  }, []);

  const shouldRender = state.currentScreen !== SCREEN_STATES.HIDDEN;

  return {
    shouldRender,
    isLoading: state.isLoading,
    leadTargets: state.cachedLeadTargets,
    ...(shouldRender && screenConfigs[state.currentScreen]),
    onZipSubmit: handleZipSubmit,
  };
};
